import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Badge,
  EmptyState,
  Page,
  Pagination,
  SearchInput,
  usePagination,
} from "../../../components/ui";
import {
  DetailHeader,
  DetailSkeleton,
  FactList,
  useStoredDetail,
} from "../../../components/detail";
import { getBroadcastInfo } from "../../../store/actions/uploadMediaAction";
import { getBroadcast } from "../../../services/broadcasts";
import { getDate } from "../../../utils/format";
import { capitalize, matches } from "../../../utils/list";

const BroadcastInfo = () => {
  const { broadcast_id } = useParams();
  const { t } = useTranslation();

  const { item: broadcast, loading } = useStoredDetail({
    load: () => getBroadcast(broadcast_id),
    setInfo: getBroadcastInfo,
    storeKey: "broadcastDetails",
    pagePath: `/dashboard/broadcast-info/${broadcast_id}`,
  });

  const [search, setSearch] = useState("");
  const users = useMemo(
    () =>
      (broadcast?.users || []).filter(
        (user) => matches(user.name, search) || matches(user.email, search)
      ),
    [broadcast, search]
  );
  const pagination = usePagination(users, 10, [search]);

  if (loading) {
    return (
      <Page>
        <DetailSkeleton />
      </Page>
    );
  }

  if (!broadcast) {
    return (
      <Page>
        <DetailHeader title={t("Broadcast Details")} />
        <EmptyState icon="ri-broadcast-line">
          {t("No broadcast found for the provided ID!")}
        </EmptyState>
      </Page>
    );
  }

  const recipients = broadcast.users || [];

  return (
    <Page>
      <DetailHeader
        title={broadcast.title}
        badges={broadcast.type && <Badge color="blue">{capitalize(broadcast.type)}</Badge>}
      />

      <div className="detail-layout">
        <section className="ui-card detail-pad">
          <div className="detail-section">
            <h3 className="detail-section-title">{t("Subject")}</h3>
            <p className="detail-description">{broadcast.subject || "—"}</p>
          </div>
          <div className="detail-section">
            <h3 className="detail-section-title">{t("Message")}</h3>
            <div
              className="detail-description detail-html"
              dangerouslySetInnerHTML={{ __html: broadcast.message }}
            />
          </div>
        </section>

        <section className="ui-card detail-pad" style={{ alignSelf: "start" }}>
          <FactList
            items={[
              { label: t("Type"), value: broadcast.type },
              { label: t("Sent"), value: getDate(broadcast.createdAt) },
              { label: capitalize(t("users")), value: recipients.length },
            ]}
          />
        </section>
      </div>

      <section className="ui-card">
        <div className="ui-toolbar">
          <h2 className="detail-card-title">{capitalize(t("users list"))}</h2>
          {recipients.length > 0 && (
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t("Search by name or email")}
            />
          )}
        </div>

        {users.length === 0 ? (
          <EmptyState icon="ri-user-search-line">{t("No Data Found")}</EmptyState>
        ) : (
          <>
            <div className="ui-table-wrap">
              <table className="ui-table">
                <thead>
                  <tr>
                    <th>{t("name")}</th>
                    <th>{t("email")}</th>
                    <th>{t("country")}</th>
                    <th>{t("gender")}</th>
                    <th>{t("package")}</th>
                    <th>{t("lang")}</th>
                  </tr>
                </thead>
                <tbody>
                  {pagination.pageItems.map((user, index) => (
                    <tr key={user._id || index}>
                      <td style={{ fontWeight: 600 }}>
                        {capitalize(user.name) || "N/A"}
                      </td>
                      <td className="ui-cell-muted">{user.email || "N/A"}</td>
                      <td>{user.country || "N/A"}</td>
                      <td>{capitalize(user.gender) || "N/A"}</td>
                      <td>{capitalize(user.subscription) || "N/A"}</td>
                      <td>{capitalize(user.language) || "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination {...pagination} onChange={pagination.setPage} />
          </>
        )}
      </section>
    </Page>
  );
};

export default BroadcastInfo;
